import { useField } from '@framework/form/field/useField.tsx';
import { StaticInput } from '@framework/form/inputs/static/StaticInput.tsx';
import { getNetworkErrorMessage } from '@framework/ui/core/errors/getNetworkErrorMessage.tsx';
import { Alert } from 'antd';
import { get, isEmpty, isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RichTextAdvancedRender } from '../../../../../inputs/rich-text-advanced/render/RichTextAdvancedRender.tsx';
import { ConfigFormItem } from '../../../../../ui/form/ConfigFormItem.tsx';
import { useAsyncService } from '../../../../expressions/repository/async/useAsyncService.ts';
import { useAsyncServiceGetData } from '../../../../expressions/repository/async/useAsyncServiceGetData.ts';
import { useWidgetScopeContext } from '../../../../form/fillable/WidgetScopeContext.tsx';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { getAsyncServiceLabel } from '../async-select/components/label/getSelectedValueLabel.ts';
import { getWidgetProps } from '../common/util/getWidgetProps.ts';
import { asyncValueWidgetObjectName } from './AsyncValueWidget.type.tsx';
import { type IAsyncValueWidget } from './IAsyncValueWidget.ts';

export type IAsyncValueWidgetComponentProps =
  IWidgetComponentProps<IAsyncValueWidget>;

export function AsyncValueWidgetComponent(
  props: IAsyncValueWidgetComponentProps,
) {
  const { widget } = props;

  const options = getWidgetProps(props);
  const { prefix } = useWidgetScopeContext();
  const { service, axiosClient } = useAsyncService(widget.serviceId);
  const { t } = useTranslation();

  // Faccio la chiamata
  const { data, error, loading, calledOnce } = useAsyncServiceGetData({
    basePath: prefix,
    client: axiosClient,
    service,
    inputs: widget.inputs,
    disabled: options.readonly,
  });

  const objectFieldName = useMemo(
    () => `${prefix}${asyncValueWidgetObjectName(widget.code)}`,
    [prefix, widget.code],
  );

  const valueField = useField(options.name);
  const objectField = useField(objectFieldName);
  const [label, setLabel] = useState(
    getAsyncServiceLabel(widget.labelFields, objectField.getValue()),
  );

  useEffect(() => {
    if (!valueField.visible) return;

    if (!calledOnce.current)
      // Se la chiamata non è stata fatta, non faccio nulla (mantengo i valori originali)
      return;
    if (loading || error) return;

    // Se è presente più di un elemento, ritorno il primo
    const singleValues = Array.isArray(data) ? data[0] : data;

    if (widget.outputs == null) return;

    // Casto i values in base allo schema dell'oggetto così da renderlo uguale al
    // valore al momento salvato a livello di struttura, chiavi e tipi, così da poterli anche confrontare.
    // e.g. se per esempio gli output non sono identici ai value (a livello di chiavi)
    // i check successivi sono considerati tutti falsi andando poi a chiamare
    // la .setValue() sui field, rendendo il form dirty senza motivo
    const values = objectField
      .getSchema()
      ?.cast(singleValues, { assert: false });

    const currentObjectValue = objectField.getValue();

    // Sia lo stato "vuoto" del campo che il risultato di un servizio senza dati
    // vanno trattati come "nessun valore". ATTENZIONE: quando il servizio non
    // restituisce dati, `objectField.getSchema().cast(undefined)` NON ritorna
    // `null`/`undefined` ma un oggetto vuoto `{}` (default Yup per `.object()`).
    // Per questo confrontiamo con `isEmpty(values)` e non con `values == null`:
    // altrimenti `{} == null` è false, nessuna guard scatta, si scrive sempre e
    // l'effect entra in loop con `syncComputedValues`.
    if (isEmpty(currentObjectValue) && isEmpty(values)) {
      // Anche in questo caso il valore principale può essere rimasto
      // valorizzato da solo (coppia disallineata, e.g. payload con il valore
      // ma senza object): va azzerato come nel ramo successivo, altrimenti
      // sopravvive senza oggetto associato.
      if (valueField.getValue() != null) {
        valueField.setValue(null);
        setLabel('');
      }
      return;
    }

    // Se l'oggetto salvato non è vuoto e il servizio non ha dati, lo annullo.
    // shouldDirty/shouldTouch: questo widget è readonly e non deve sporcare il form,
    // tuttavia se il servizio non ritorna dati, il valore salvato in precedenza
    // va annullato e quindi devo fare un setValue() per rimuoverlo ed è un
    // cambiamento che deve essere salvato, quindi non posso usare shouldDirty/shouldTouch: false.
    if (!isEmpty(currentObjectValue) && isEmpty(values)) {
      valueField.setValue(null);
      setLabel('');
      objectField.setValue({});
      return;
    }

    // Il valore che il widget dovrebbe avere in base all'oggetto del servizio.
    // Va castato con lo schema del campo principale (stringa): gli output
    // dell'oggetto sono tipizzati (number, boolean, ...) mentre nel form il
    // valore viene riportato a stringa da syncComputedValues. Senza cast il
    // confronto sotto fallirebbe sempre (e.g. 1 vs '1'), riscrivendo il campo
    // a ogni render ed entrando in loop.
    const rawValue = get(values, widget.valueField);
    const newValue =
      valueField.getSchema()?.cast(rawValue, { assert: false }) ?? rawValue;

    // Se non ci sono differenze tra i valori, non faccio nulla.
    // Value e object viaggiano sempre insieme: controlliamo che ANCHE il
    // valore principale sia allineato all'oggetto, perché può andare perso
    // da solo (e.g. unregister del FormItem al mount con widget non visibile,
    // che rimuove solo la chiave principale). Se uno dei due è disallineato,
    // riscriviamo entrambi.
    if (
      isEqual(values, currentObjectValue) &&
      isEqual(valueField.getValue(), newValue)
    ) {
      return;
    }

    // Questo widget è readonly, come i widget calcolati. Se cambia non dovrebbe influenzare
    // lo stato del form. L'unico momento in cui una sua modifica dovrebbe influenzare il form
    // è nel caso un suo parametro venga modificato (per la query) ma quei parametri sono dinamici
    // solo in caso di reference ad altri widget, che a loro volta renderebbero _comunque_ il
    // form dirty, rendendo quindi inutile questa operazione.
    // In questo modo, non andiamo a rendere dirty il form al primo render di una nuova sezione
    // (quando il campo passa da undefined a un valore ritornato come "default" dal servizio, nel caso)
    valueField.setValue(newValue, { shouldDirty: false, shouldTouch: false });
    objectField.setValue(values, { shouldDirty: false, shouldTouch: false });

    // Andiamo a costruire la label con tutti i campi configurati
    setLabel(getAsyncServiceLabel(widget.labelFields, values));
  }, [
    calledOnce,
    data,
    error,
    loading,
    objectField,
    valueField,
    widget.labelFields,
    widget.valueField,
    widget.outputs,
  ]);

  if (error) {
    return (
      <Alert
        message={getNetworkErrorMessage(
          error,
          t(
            'async-value-widget.error-message',
            'Si è verificato un errore, riprova più tardi',
          ),
        )}
        type="error"
      />
    );
  }

  return (
    <ConfigFormItem
      hidden={widget.layout?.hide}
      name={options.name}
      label={<RichTextAdvancedRender inline text={options.label} />}
      help={
        options?.helpText ? (
          <RichTextAdvancedRender inline text={options?.helpText} />
        ) : undefined
      }
      layout={{ hideVisualLabel: options.hideLabel }}
      readonly={options.readonly}
      widget={props.widget}
    >
      <StaticInput
        value={label}
        removeBorder={widget.layout?.removeBorder}
        loading={loading}
      />
    </ConfigFormItem>
  );
}
